import React, { useEffect, useState } from 'react';
import { Calendar, MapPin, DollarSign, Package, X, FileText, FilePlus } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
import { InvoiceManager } from './InvoiceManager';
interface RentalRequest {
  id: string;
  date: string;
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    shippingLocation?: string;
    eventName?: string;
  };
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
  };
  total: number;
  subtotal: number;
  tax: number;
}
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  shippingLocation: string;
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  added: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
  };
}
export function AdminPanel({
  onClose
}: {
  onClose: () => void;
}) {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'rentals' | 'invoices'>('rentals');
  const [invoices, setInvoices] = useState<any[]>([]);
  useEffect(() => {
    loadRentals();
    loadInvoices();
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    // Cleanup: restore body scroll when modal closes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  const loadInvoices = () => {
    const stored = localStorage.getItem('invoices');
    if (stored) {
      try {
        setInvoices(JSON.parse(stored));
      } catch (error) {
        console.error('Error loading invoices:', error);
      }
    }
  };
  const createInvoiceFromRental = (rental: Rental) => {
    // Check if invoice already exists
    const existingInvoice = invoices.find(inv => inv.rentalId === rental.id);
    if (existingInvoice) {
      alert('An invoice already exists for this rental');
      return;
    }
    const lineItems: any[] = [];
    if (rental.selections.fullGen3Ring > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Full Gen 3 Ring',
        quantity: rental.selections.fullGen3Ring,
        price: 900,
        total: rental.selections.fullGen3Ring * 900
      });
    }
    if (rental.selections.gen3Body > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Body',
        quantity: rental.selections.gen3Body,
        price: 700,
        total: rental.selections.gen3Body * 700
      });
    }
    if (rental.selections.gen3Head > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Head',
        quantity: rental.selections.gen3Head,
        price: 425,
        total: rental.selections.gen3Head * 425
      });
    }
    if (rental.selections.gen3JudgesOnly > 0) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'Gen 3 Judges Only',
        quantity: rental.selections.gen3JudgesOnly,
        price: 350,
        total: rental.selections.gen3JudgesOnly * 350
      });
    }
    if (rental.selections.requireTech) {
      lineItems.push({
        id: Math.random().toString(),
        description: 'On-site Technician',
        quantity: 1,
        price: 500,
        total: 500
      });
    }
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    const generateInvoiceNumber = () => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `INV-${year}${month}-${random}`;
    };
    const newInvoice = {
      id: Date.now().toString(),
      rentalId: rental.id,
      invoiceNumber: generateInvoiceNumber(),
      customerName: rental.name,
      customerEmail: rental.email,
      status: 'Draft',
      lineItems,
      subtotal,
      discount: 0,
      tax: 0,
      total: subtotal,
      amountPaid: 0,
      balance: subtotal,
      created: new Date().toISOString()
    };
    const updatedInvoices = [...invoices, newInvoice];
    localStorage.setItem('invoices', JSON.stringify(updatedInvoices));
    setInvoices(updatedInvoices);
    // Switch to invoices tab
    setActiveTab('invoices');
  };
  const hasInvoice = (rentalId: string) => {
    return invoices.some(inv => inv.rentalId === rentalId);
  };
  const loadRentals = async () => {
    setLoading(true);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report',
        criteria: ''
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedRentals: Rental[] = resp.map((item: any) => ({
        id: item.ID,
        name: item.Name,
        email: item.Email,
        phone: item.Phone_Number,
        eventName: item.Event_Name || 'N/A',
        shippingLocation: item.Shipping_Location || 'N/A',
        proposedEventDate: item.Proposed_Event_Date || '',
        alternateDate: item.Alternate_Date || '',
        status: item.Status || 'Pending',
        total: parseFloat(item.Total_Amount || 0),
        added: item.added,
        selections: {
          gen3Body: item.Gen3_Body_Quantity || 0,
          gen3Head: item.Gen3_Head_Quantity || 0,
          gen3JudgesOnly: item.Gen3_Judges_Only_Quantity || 0,
          fullGen3Ring: item.Full_Gen3_Ring_Quantity || 0,
          requireTech: item.Requires_Technician === 'true'
        }
      }));
      setRentals(loadedRentals);
    } catch (error) {
      console.error('Error loading rentals:', error);
    } finally {
      setLoading(false);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Deposit':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Paid in Full':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Pending':
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };
  const filteredRentals = rentals.filter(rental => {
    if (filter === 'all') return true;
    return rental.status === filter;
  });
  const groupedByDate = filteredRentals.reduce((acc, rental) => {
    const date = rental.proposedEventDate || 'No Date';
    if (!acc[date]) acc[date] = [];
    acc[date].push(rental);
    return acc;
  }, {} as Record<string, Rental[]>);
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    return new Date(a).getTime() - new Date(b).getTime();
  });
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-44">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl my-8 flex flex-col max-h-[90vh]" data-id="element-45">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 flex-shrink-0" data-id="element-46">
          <div data-id="element-47">
            <h2 className="text-2xl font-bold text-slate-800" data-id="element-48">Admin Panel</h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-49">
              Manage rentals and invoices
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-50">
            <X size={24} className="text-slate-600" data-id="element-51" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 flex-shrink-0" data-id="element-52">
          <button onClick={() => setActiveTab('rentals')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors relative ${activeTab === 'rentals' ? 'text-blue-600' : 'text-slate-600 hover:text-slate-800'}`} data-id="element-53">
            <Calendar size={18} data-id="element-54" />
            Rentals
            {activeTab === 'rentals' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-55"></div>}
          </button>
          <button onClick={() => {
          setActiveTab('invoices');
          loadInvoices(); // Refresh invoices when switching tabs
        }} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors relative ${activeTab === 'invoices' ? 'text-blue-600' : 'text-slate-600 hover:text-slate-800'}`} data-id="element-56">
            <FileText size={18} data-id="element-57" />
            Invoices
            {activeTab === 'invoices' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" data-id="element-58"></div>}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0" data-id="element-59">
          {activeTab === 'rentals' ? <>
              {/* Filters */}
              <div className="flex gap-2 p-6 border-b border-slate-200 overflow-x-auto" data-id="element-60">
                <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-61">
                  All ({rentals.length})
                </button>
                <button onClick={() => setFilter('Pending')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Pending' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-62">
                  Pending (
                  {rentals.filter(r => r.status === 'Pending').length})
                </button>
                <button onClick={() => setFilter('Confirmed')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Confirmed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-63">
                  Confirmed (
                  {rentals.filter(r => r.status === 'Confirmed').length})
                </button>
                <button onClick={() => setFilter('Deposit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Deposit' ? 'bg-yellow-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-64">
                  Deposit (
                  {rentals.filter(r => r.status === 'Deposit').length})
                </button>
                <button onClick={() => setFilter('Paid in Full')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Paid in Full' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-65">
                  Paid in Full (
                  {rentals.filter(r => r.status === 'Paid in Full').length})
                </button>
              </div>

              {/* Rentals Content */}
              <div className="p-6" data-id="element-66">
                {loading ? <div className="flex items-center justify-center h-64" data-id="element-67">
                    <div className="text-center" data-id="element-68">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-69"></div>
                      <p className="text-slate-600" data-id="element-70">Loading rentals...</p>
                    </div>
                  </div> : filteredRentals.length === 0 ? <div className="flex items-center justify-center h-64" data-id="element-71">
                    <div className="text-center" data-id="element-72">
                      <Calendar size={48} className="text-slate-300 mx-auto mb-4" data-id="element-73" />
                      <p className="text-slate-600 text-lg font-medium" data-id="element-74">
                        No rentals found
                      </p>
                      <p className="text-slate-500 text-sm mt-1" data-id="element-75">
                        {filter !== 'all' ? 'Try changing the filter' : 'Rentals will appear here'}
                      </p>
                    </div>
                  </div> : <div className="space-y-8" data-id="element-76">
                    {sortedDates.map(date => <div key={date} data-id="element-77">
                        <div className="flex items-center gap-3 mb-4" data-id="element-78">
                          <Calendar size={20} className="text-blue-600" data-id="element-79" />
                          <h3 className="text-lg font-semibold text-slate-800" data-id="element-80">
                            {date === 'No Date' ? 'No Date Set' : new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                          </h3>
                          <span className="text-sm text-slate-500" data-id="element-81">
                            ({groupedByDate[date].length} rental
                            {groupedByDate[date].length !== 1 ? 's' : ''})
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-82">
                          {groupedByDate[date].map(rental => <div key={rental.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow" data-id="element-83">
                              <div className="flex items-start justify-between mb-3" data-id="element-84">
                                <div className="flex-1 cursor-pointer" onClick={() => setSelectedRental(rental)} data-id="element-85">
                                  <h4 className="font-semibold text-slate-800" data-id="element-86">
                                    {rental.name}
                                  </h4>
                                  <p className="text-sm text-slate-500" data-id="element-87">
                                    {rental.email}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-88">
                                  {rental.status}
                                </span>
                              </div>

                              <div className="space-y-2 mb-3 cursor-pointer" onClick={() => setSelectedRental(rental)} data-id="element-89">
                                <div className="flex items-center text-sm text-slate-600" data-id="element-90">
                                  <Package size={14} className="mr-2 text-slate-400" data-id="element-91" />
                                  <span className="font-medium" data-id="element-92">
                                    {rental.eventName}
                                  </span>
                                </div>
                                <div className="flex items-center text-sm text-slate-600" data-id="element-93">
                                  <MapPin size={14} className="mr-2 text-slate-400" data-id="element-94" />
                                  <span className="truncate" data-id="element-95">
                                    {rental.shippingLocation}
                                  </span>
                                </div>
                                <div className="flex items-center text-sm text-slate-600" data-id="element-96">
                                  <DollarSign size={14} className="mr-2 text-slate-400" data-id="element-97" />
                                  <span className="font-semibold" data-id="element-98">
                                    ${rental.total.toFixed(2)}
                                  </span>
                                </div>
                              </div>

                              <div className="pt-3 border-t border-slate-100 mb-3" data-id="element-99">
                                <div className="flex flex-wrap gap-2 text-xs" data-id="element-100">
                                  {rental.selections.fullGen3Ring > 0 && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded" data-id="element-101">
                                      {rental.selections.fullGen3Ring} Ring
                                      {rental.selections.fullGen3Ring !== 1 ? 's' : ''}
                                    </span>}
                                  {rental.selections.gen3Body > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-102">
                                      {rental.selections.gen3Body} Body
                                    </span>}
                                  {rental.selections.gen3Head > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-103">
                                      {rental.selections.gen3Head} Head
                                    </span>}
                                  {rental.selections.requireTech && <span className="bg-red-50 text-red-700 px-2 py-1 rounded" data-id="element-104">
                                      Tech
                                    </span>}
                                </div>
                              </div>

                              {/* Create Invoice Button */}
                              <button onClick={() => createInvoiceFromRental(rental)} disabled={hasInvoice(rental.id)} className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${hasInvoice(rental.id) ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-105">
                                {hasInvoice(rental.id) ? <>
                                    <FileText size={14} data-id="element-106" />
                                    Invoice Created
                                  </> : <>
                                    <FilePlus size={14} data-id="element-107" />
                                    Create Invoice
                                  </>}
                              </button>
                            </div>)}
                        </div>
                      </div>)}
                  </div>}
              </div>
            </> : <div className="p-6" data-id="element-108">
              <InvoiceManager rentals={rentals} data-id="element-109" />
            </div>}
        </div>
      </div>

      {/* Rental Detail Modal */}
      {selectedRental && <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4" data-id="element-110">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-111">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between" data-id="element-112">
              <h3 className="text-xl font-bold text-slate-800" data-id="element-113">
                Rental Details
              </h3>
              <button onClick={() => setSelectedRental(null)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-114">
                <X size={20} className="text-slate-600" data-id="element-115" />
              </button>
            </div>
            <div className="p-6 space-y-6" data-id="element-116">
              <div data-id="element-117">
                <label className="text-sm font-medium text-slate-500" data-id="element-118">
                  Status
                </label>
                <div className="mt-1" data-id="element-119">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(selectedRental.status)}`} data-id="element-120">
                    {selectedRental.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-121">
                <div data-id="element-122">
                  <label className="text-sm font-medium text-slate-500" data-id="element-123">
                    Name
                  </label>
                  <p className="mt-1 text-slate-800 font-medium" data-id="element-124">
                    {selectedRental.name}
                  </p>
                </div>
                <div data-id="element-125">
                  <label className="text-sm font-medium text-slate-500" data-id="element-126">
                    Phone
                  </label>
                  <p className="mt-1 text-slate-800" data-id="element-127">{selectedRental.phone}</p>
                </div>
              </div>

              <div data-id="element-128">
                <label className="text-sm font-medium text-slate-500" data-id="element-129">
                  Email
                </label>
                <p className="mt-1 text-slate-800" data-id="element-130">{selectedRental.email}</p>
              </div>

              <div data-id="element-131">
                <label className="text-sm font-medium text-slate-500" data-id="element-132">
                  Event Name
                </label>
                <p className="mt-1 text-slate-800 font-medium" data-id="element-133">
                  {selectedRental.eventName}
                </p>
              </div>

              <div data-id="element-134">
                <label className="text-sm font-medium text-slate-500" data-id="element-135">
                  Shipping Location
                </label>
                <p className="mt-1 text-slate-800" data-id="element-136">
                  {selectedRental.shippingLocation}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-137">
                <div data-id="element-138">
                  <label className="text-sm font-medium text-slate-500" data-id="element-139">
                    Proposed Date
                  </label>
                  <p className="mt-1 text-slate-800" data-id="element-140">
                    {selectedRental.proposedEventDate ? new Date(selectedRental.proposedEventDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
                <div data-id="element-141">
                  <label className="text-sm font-medium text-slate-500" data-id="element-142">
                    Alternate Date
                  </label>
                  <p className="mt-1 text-slate-800" data-id="element-143">
                    {selectedRental.alternateDate ? new Date(selectedRental.alternateDate).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
              </div>

              <div data-id="element-144">
                <label className="text-sm font-medium text-slate-500 mb-2 block" data-id="element-145">
                  Equipment
                </label>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2" data-id="element-146">
                  {selectedRental.selections.fullGen3Ring > 0 && <div className="flex justify-between" data-id="element-147">
                      <span className="text-slate-700" data-id="element-148">Full Gen 3 Ring</span>
                      <span className="font-medium text-slate-800" data-id="element-149">
                        {selectedRental.selections.fullGen3Ring}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3Body > 0 && <div className="flex justify-between" data-id="element-150">
                      <span className="text-slate-700" data-id="element-151">Gen 3 Body</span>
                      <span className="font-medium text-slate-800" data-id="element-152">
                        {selectedRental.selections.gen3Body}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3Head > 0 && <div className="flex justify-between" data-id="element-153">
                      <span className="text-slate-700" data-id="element-154">Gen 3 Head</span>
                      <span className="font-medium text-slate-800" data-id="element-155">
                        {selectedRental.selections.gen3Head}
                      </span>
                    </div>}
                  {selectedRental.selections.gen3JudgesOnly > 0 && <div className="flex justify-between" data-id="element-156">
                      <span className="text-slate-700" data-id="element-157">Judges Only</span>
                      <span className="font-medium text-slate-800" data-id="element-158">
                        {selectedRental.selections.gen3JudgesOnly}
                      </span>
                    </div>}
                  {selectedRental.selections.requireTech && <div className="flex justify-between" data-id="element-159">
                      <span className="text-slate-700" data-id="element-160">On-site Technician</span>
                      <span className="font-medium text-red-600" data-id="element-161">Required</span>
                    </div>}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-162">
                <div className="flex justify-between items-center" data-id="element-163">
                  <span className="text-blue-800 font-medium" data-id="element-164">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-blue-900" data-id="element-165">
                    ${selectedRental.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div data-id="element-166">
                <label className="text-sm font-medium text-slate-500" data-id="element-167">
                  Submitted
                </label>
                <p className="mt-1 text-slate-600 text-sm" data-id="element-168">
                  {new Date(selectedRental.added).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}